<template>
  <v-dialog
    v-model="inputValue"
    min-width="320px"
    max-width="800px"
    :close-on-content-click="false"
    offset-y
    scrollable
  >
    <v-card>
        <v-card-title class="text-h5 lighten-2">
          <v-row justify="space-between">
            Compose Email
            <Popover>
              <v-btn icon>
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
              <template #content>
                <v-list dense>
                  <v-list-item>
                    <v-btn color="primary" :disabled="isDisableSaveTemplate" :loading="isSending" block @click="saveTemplate">Save</v-btn>
                  </v-list-item>
                  <v-list-item>
                    <v-btn color="error" block :disabled="isDeleteTemplateDisabled" @click="confirmDelete = true;">Delete</v-btn>
                  </v-list-item>
                </v-list>
              </template>
            </Popover>
          </v-row>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-container>
            <v-row class="email-templates" dense>
              <v-col>
                <v-progress-circular
                  v-show="loadingTemplates"
                  color="warning"
                  indeterminate
                  :size="34"
                />
                <template v-if="!loadingTemplates">
                  <v-container>
                    <v-row justify="space-between" align="items-center">
                      <p>Choose email template</p>
                      <v-btn color="primary" @click="handleCustomMessage" outlined><v-icon>mdi-plus</v-icon> Add</v-btn>
                    </v-row>
                  </v-container>
                  <v-row dense>
                    <v-col
                      v-for="template in templateList"
                      :key="template.label"
                      cols="4"
                    >
                      <toggle-btn
                        :text="template.label"
                        :active="selectedTemplate && selectedTemplate.label === template.label"
                        @click="handleSelectTemplate(template)"
                      />
                    </v-col>
                    <v-col cols="4">
                      <toggle-btn
                        v-if="templates.length > 11"
                        :text="isExpanded ? 'Hide' : `Show All (${templates.length})`"
                        @click="expandTemplateList">
                      </toggle-btn>
                    </v-col>
                  </v-row>
                  <CustTemplateEmail
                    v-if="!!selectedTemplate"
                    ref="editor"
                    :form-errors="formErrors"
                    :item="selectedTemplate"
                  />
                </template>
              </v-col>

              <!-- Email Log Section -->
              <v-col>
                <p>Email Logs</p>
                <v-simple-table v-if="data && data.sent_emails.length">
                  <template v-slot:default>
                    <tbody>
                      <tr
                        v-for="(item, i) in data.sent_emails"
                        :key="i"
                      >
                        <td>{{ item.submitted_at | formatDate }}</td>
                        <td>{{ item.notification_label }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <p v-else>No logs found</p>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="inputValue = false"
            outlined
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="handleSubmit"
            :loading="isSending"
            :disabled="!selectedTemplate"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-dialog
        v-model="confirmDelete"
        title="Delete Email Template"
        max-width="400px"
        description="Are you sure you want to delete this email template?"
      >
        <v-card>
          <v-card-title>Delete Email Template</v-card-title>
          <v-card-text>Are you sure you want to delete this email template?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="confirmDelete = false" text :loading="isSending">
              Cancel
            </v-btn>
            <v-btn
              text
              color="error"
              :elevation="0"
              @click="deleteTemplate"
              :loading="isSending"
              :disabled="!selectedTemplate"
            >
              Delete Email
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </v-dialog>
</template>

<script>
import applicationTaskList from '@/repositories/applicationTaskList';
import CustTemplateEmail from '@/app/admin/modules/candidates/components/candidates/CandidateCustomETemp.vue';
import FormErrors from '@/util/form-errors';
import Popover from '@/components/Popover.vue';

export default {
  components: {
    ToggleBtn: () => import('@/components/global/OutlinedLabel.vue'),
    CustTemplateEmail,
    Popover,
  },
  props: {
    data: {
      default: () => ({}),
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
      required: true,
    },
  },
  data() {
    return {
      loadingTemplates: false,
      isSending: false,
      templates: [],
      isNewTemplate: false,
      logs: [],
      selectedTemplate: null,
      templateList: [],
      formErrors: new FormErrors(),
      confirmDelete: false,
    };
  },
  watch: {
    value(val) {
      if (val) {
        this.fetchTemplates();
      } else {
        this.selectedTemplate = null;
      }
    },
  },
  computed: {
    inputValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    isExpanded() {
      return this.templates.length === this.templateList.length;
    },
    isDeleteTemplateDisabled() {
      return this.isNewTemplate || !this.selectedTemplate || this.selectedTemplate && this.selectedTemplate.is_system_template;
    },
    isDisableSaveTemplate() {
      return this.selectedTemplate && this.selectedTemplate.is_system_template
    },
  },
  methods: {
    async fetchTemplates() {
      this.loadingTemplates = true;
      try {
        const [systemTemplates, editableTemplates] = await Promise.all([
          applicationTaskList.fetchSystemTemplates(),
          applicationTaskList.fetchAllTemplates()
        ]);

        const result = systemTemplates.map((item) => ({ ...item, is_system_template: true })).concat(editableTemplates);
        this.templates = result;
        this.templateList = result.slice(0, 11);
      } catch (e) {
        this.$notify({text: 'Error fetching email templates', type: 'error'});
      }
      this.loadingTemplates = false;
    },
    expandTemplateList() {
      if (!this.isExpanded) {
        this.templateList = this.templateList.concat(this.templates.slice(11));
      } else {
        this.templateList = this.templates.slice(0, 11);
      }
    },
    async handleSubmit() {
      this.isSending = true;
      try {
        const values = this.$refs.editor.getData();
        await applicationTaskList.sendEmailToApplicant({
          ...values,
          content: values.content ?? '',
          subject: values.subject ?? '',
          notification_label: values.label,
          ids: [this.data.application_id],
        });
        this.reset();
        this.$emit('emailSent');
        this.$notify({text: 'Email successfully sent', type: 'success'});
        this.inputValue = false;
      } catch (e) {
        this.$notify({text: 'Email failed to send', type: 'error'});
      }
      this.isSending = false;
    },
    handleSelectTemplate(tpl) {
      this.selectedTemplate = {...tpl};
      this.isNewTemplate = false;
      this.$nextTick(() => {
        this.$refs.editor.setData();
      })
    },
    handleCustomMessage() {
      this.selectedTemplate = {
        label: '',
        subject: '',
        content: '',
        readonly: false,
      };
      this.isNewTemplate = true;
      this.$nextTick(() => {
        this.$refs.editor.setData();
      })
    },
    async saveTemplate() {
      this.isSending = true;
      try {
        const values = this.$refs.editor.getData();
        const payload = {
          content: values.content ?? '',
          subject: values.subject ?? '',
          label: values.label,
        };

        if (this.selectedTemplate.id) {
          await applicationTaskList.updateTemplates({
            ...payload,
            id: this.selectedTemplate.id,
          })
        } else {
          await applicationTaskList.saveTemplates(payload);
        }

        this.isNewTemplate = false;
        this.$notify({text: 'Template succesfully added', type: 'success'});
        this.fetchTemplates();
      } catch (e) {
        this.$notify({text: 'Failed to save template', type: 'error'});
      }
      this.isSending = false;
    },
    reset() {
      this.selectedTemplate = {
        label: '',
        subject: '',
        content: '',
        readonly: false,
      };
      this.$refs.editor.reset();
    },
    async deleteTemplate() {
      this.isSending = true;
      try {
        await applicationTaskList.deleteEmailTemplate(this.selectedTemplate.id);
        this.$notify({text: 'Template succesfully deleted', type: 'success'});
        this.reset();
        this.fetchTemplates();
        this.confirmDelete = false;
      } catch (e) {
        this.$notify({text: 'Failed to delete template', type: 'error'});
      }
      this.isSending = false;
    },
  },
};
</script>
